import { isAgencyChattable } from '@saaga/shared/utils/agency-visibility.util'
import { type ReactNode, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { DeepLinkBuilderDialog } from '@/components/agencies/deep-link-builder-dialog.organism'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { agencyRoutes, orgRoutes } from '@/routes/org-routes.const'

import { AgencyDetailError } from './agency-detail-error.atom'
import { AgencyDetailLoading } from './agency-detail-loading.atom'
import { AgencyDetailPanelsView } from './agency-detail-panels.view'
import { AgencyNotFoundView } from './agency-not-found.view'
import { getTabFromPath } from './agency-tab.util'
import { useAgencyDetail } from './use-agency-detail.hook'
import { useAgencyVisibilityActions } from './use-agency-visibility-actions.hook'
import { useDeepLinkBuilder } from './use-deep-link-builder.hook'

const useAgencyDetailViewState = () => {
  const { agency, agencyResponse, isLoading, isError, handleBack, handleStartChat } =
    useAgencyDetail()
  const location = useLocation()
  const navigate = useNavigate()
  const orgSlug = useOrgSlug()
  const activeTab = getTabFromPath(location.pathname)
  const isOwnerContext = activeTab === 'studio' || activeTab === 'organization'
  const visibilityActions = useAgencyVisibilityActions({ slug: agency?.slug ?? '' })
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const deepLinkBuilder = useDeepLinkBuilder({
    agencySlug: agency?.slug ?? '',
    isOpen: isShareDialogOpen,
  })

  const handleEdit = (): void => {
    if (!agency) return
    navigate(
      `/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.studio}/${agency.slug}/${agencyRoutes.edit}`
    )
  }

  return {
    agency,
    agencyResponse,
    isLoading,
    isError,
    handleBack,
    handleStartChat,
    isOwnerContext,
    visibilityActions,
    isShareDialogOpen,
    setIsShareDialogOpen,
    deepLinkBuilder,
    handleEdit,
  }
}

export const AgencyDetailView = (): ReactNode => {
  const state = useAgencyDetailViewState()

  if (state.isLoading) return <AgencyDetailLoading />
  if (state.isError) return <AgencyDetailError />
  if (!state.agency || !state.agencyResponse) return <AgencyNotFoundView />

  const isShareable = state.agency.visibility === 'public'

  return (
    <TooltipProvider>
      <div className="agency-detail-wrapper flex h-full flex-col">
        <AgencyDetailPanelsView
          agency={state.agency}
          agencyResponse={state.agencyResponse}
          isOwnerContext={state.isOwnerContext}
          visibilityActions={state.visibilityActions}
          onEdit={state.handleEdit}
          onStartChat={
            isAgencyChattable(state.agency.visibility) ? state.handleStartChat : undefined
          }
          onShareClick={isShareable ? () => state.setIsShareDialogOpen(true) : undefined}
          onBack={state.handleBack}
        />
        {isShareable && (
          <DeepLinkBuilderDialog
            isOpen={state.isShareDialogOpen}
            onOpenChange={state.setIsShareDialogOpen}
            agencyName={state.agency.name}
            templateList={state.deepLinkBuilder.templateList}
            selectedTemplateId={state.deepLinkBuilder.selectedTemplateId}
            onTemplateSelect={state.deepLinkBuilder.onTemplateSelect}
            parsedVariableList={state.deepLinkBuilder.parsedVariableList}
            form={state.deepLinkBuilder.form}
            urlPreview={state.deepLinkBuilder.urlPreview}
            onCopy={state.deepLinkBuilder.onCopy}
            isLoading={state.deepLinkBuilder.isLoading}
          />
        )}
      </div>
    </TooltipProvider>
  )
}
